import { useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";
import { getApiConfig } from "./apiConfig";

const createAxiosInstance = (apiType = "legacy-timepieces") => {
  const config = getApiConfig(apiType);
  const instance = axios.create(config);

  // Request interceptor
  instance.interceptors.request.use(
    (config) => {
      // You can add additional logic here (e.g., refresh token)
      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  // Response interceptor
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      // Handle common errors (401, 403, 500, etc.)
      if (error.response?.status === 401) {
        // Handle unauthorized (e.g., redirect to login)
        // window.location.href = '/login';
      }
      return Promise.reject(error);
    },
  );

  return instance;
};

export const useApi = (url, options = {}) => {
  const {
    method = "GET",
    initialData = null,
    executeOnMount = false,
    onSuccess = null,
    onError = null,
    apiType = "legacy-timepiecess",
  } = options;

  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const axiosInstance = useRef(createAxiosInstance(apiType));
  const isMounted = useRef(true);
  const hasExecutedOnMount = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const execute = useCallback(
    async (config = {}) => {
      const { body = null, params = {}, query = {}, headers = {} } = config;

      setLoading(true);
      setError(null);

      try {
        let finalUrl = url;
        if (params && Object.keys(params).length > 0) {
          Object.entries(params).forEach(([key, value]) => {
            finalUrl = finalUrl.replace(`:${key}`, value);
          });
        }

        const requestConfig = {
          url: finalUrl,
          method: method.toUpperCase(),
          headers: {
            ...getApiConfig().headers,
            ...headers,
          },
        };

        if (query && Object.keys(query).length > 0) {
          requestConfig.params = query;
        }

        if (["POST", "PUT", "PATCH"].includes(method.toUpperCase()) && body) {
          requestConfig.data = body;
        }

        const response = await axiosInstance.current.request(requestConfig);

        const responseData = response.data;

        if (onSuccess) {
          onSuccess(responseData);
          setLoading(false);
        }

        if (isMounted.current) {
          setData(responseData);
          setLoading(false);
        }

        return { data: responseData, error: null };
      } catch (err) {
        const errorMessage =
          err.response?.data?.message || err.message || "An error occurred";
        const errorData = {
          message: errorMessage,
          status: err.response?.status,
          data: err.response?.data,
        };

        if (onError) {
          onError(errorData);
          setLoading(false);
        }

        if (isMounted.current) {
          setError(errorData);
          setLoading(false);
        }

        return { data: null, error: errorData };
      }
    },
    [url, method, onSuccess, onError],
  );

  useEffect(() => {
    if (executeOnMount && !hasExecutedOnMount.current) {
      hasExecutedOnMount.current = true;
      execute();
    }
  }, [executeOnMount, execute]);

  return {
    data,
    loading,
    error,
    execute,
  };
};

export const useGet = (url, options = {}) => {
  return useApi(url, { method: "GET", ...options });
};

export const usePost = (url, options = {}) => {
  return useApi(url, { method: "POST", ...options });
};

export const usePut = (url, options = {}) => {
  return useApi(url, { method: "PUT", ...options });
};

export const usePatch = (url, options = {}) => {
  return useApi(url, { method: "PATCH", ...options });
};

export const useDelete = (url, options = {}) => {
  return useApi(url, { method: "DELETE", ...options });
};

export const useMultipleApi = () => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  //Execute multiple API calls in parallel
  const executeAll = async (executeFunctions) => {
    setLoading(true);
    setErrors([]);

    try {
      // Execute all functions and get their promises
      const promises = executeFunctions.map((fn) => {
        // Call the function to get the promise
        const result = fn();
        // Ensure it's a promise
        return result instanceof Promise ? result : Promise.resolve(result);
      });

      const results = await Promise.allSettled(promises);

      const failedRequests = results
        .map((result, index) => {
          if (result.status === "rejected") {
            return {
              index,
              error: result.reason?.error ||
                result.reason || { message: "Unknown error" },
            };
          }
          return null;
        })
        .filter(Boolean);

      setErrors(failedRequests);
      setLoading(false);

      // Return the data from successful requests, null for failed ones
      return results.map((result) => {
        if (result.status === "fulfilled") {
          // If result has data property (from our execute function), return it
          return result.value?.data !== undefined
            ? result.value.data
            : result.value;
        }
        return null;
      });
    } catch (err) {
      setLoading(false);
      setErrors([{ index: -1, error: err }]);
      throw err;
    }
  };

  return { loading, errors, executeAll };
};

export default useApi;
